import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "./ImportComponentStyles";
import styled from "styled-components";
import axios from "axios";
import { getDB, dbPut } from "../../db";
import { FaTrash, FaCheck, FaTimes } from "react-icons/fa";

/* ===== helpers & stałe ===== */

const monthNames = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  color: #fff;
  background: ${({ $ok }) => ($ok ? "#16a34a" : "#dc2626")}; /* green / red */
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.08);
  svg {
    width: 12px;
    height: 12px;
  }
`;

const StickyTableWrap = styled.div`
  position: relative;
  overflow-x: auto; /* table can scroll horizontally if needed */
  /* no overflow-y here: we want the PAGE to scroll */
`;

const MonthsTable = styled(Table)`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 16px;

  thead ${TableRow} {
    background: linear-gradient(180deg, #f7f9fc 0%, #eef3fb 100%);
  }

  /* sticky on the header cells */
  thead ${TableHeader} {
    position: sticky;
    top: var(--sticky-offset, 0px); /* set offset if you have a fixed topbar */
    z-index: 5;
    background: #f7f9fc; /* solid bg so it doesn’t show rows underneath */
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #334155;
    padding: 14px 16px;
    border-bottom: 2px solid #d8e3f6;
  }

  tbody ${TableRow}:nth-child(odd) {
    background: #fcfdff;
  }
  tbody ${TableRow}:hover {
    background: #f2f7ff;
    transition: background 0.15s;
  }
  tbody ${TableCell} {
    padding: 12px 16px;
    border-bottom: 1px solid #eef2f7;
  }

  @media (max-width: 640px) {
    thead {
      display: none;
    }
    /* ... your mobile card styles remain ... */
  }
`;

const formatDatePL = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("pl-PL");
  } catch {
    return "—";
  }
};

/* ===== styled components ===== */

const SectionTitle = styled.h2`
  margin: 0 0 14px;
`;

const TabsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 20px;
`;

const IconButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  color: #b91c1c;
  padding: 6px;
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    background: #fee2e2;
    color: #7f1d1d;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Tab = styled.button`
  appearance: none;
  border: 1px solid ${({ $active }) => ($active ? "#2b6cb0" : "#dbe3ef")};
  background: ${({ $active }) => ($active ? "#e6f0ff" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#1e3a8a" : "#344155")};
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease-in-out;
  &:hover {
    border-color: #2b6cb0;
    background: #f2f7ff;
  }
`;

const HistoryScroll = styled.div`
  max-height: 480px;
  overflow: auto;
  border-radius: 12px;
  border: 1px solid #e8eef7;
  background: #fff;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
  &::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const CardNoClip = styled(Card)`
  overflow: visible; /* let sticky headers escape */
`;

/* Tabela ze sticky header + respons */
const HistoryTable = styled(Table)`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  thead ${TableRow} {
    background: linear-gradient(180deg, #f7f9fc 0%, #eef3fb 100%);
  }

  thead ${TableHeader} {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f7f9fc;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #334155;
    padding: 14px 16px;
    border-bottom: 2px solid #d8e3f6;
  }

  tbody ${TableRow}:nth-child(odd) {
    background: #fcfdff;
  }
  tbody ${TableRow}:hover {
    background: #f2f7ff;
    transition: background 0.15s;
  }
  tbody ${TableCell} {
    padding: 12px 16px;
    border-bottom: 1px solid #eef2f7;
  }

  @media (max-width: 640px) {
    thead {
      display: none;
    }
    tbody ${TableRow} {
      display: block;
      background: #fff;
      margin: 10px 0;
      border: 1px solid #eef2f7;
      border-radius: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }
    tbody ${TableCell} {
      display: grid;
      grid-template-columns: 140px 1fr;
      gap: 8px;
      padding: 10px 12px;
      border: none;
      border-bottom: 1px solid #f1f4f8;
    }
    tbody ${TableCell}:last-child {
      border-bottom: none;
    }
    tbody ${TableCell}::before {
      content: attr(data-label);
      font-weight: 700;
      color: #4b5563;
    }
  }
`;

const UserChip = styled.span`
  display: inline-block;
  background: #eef4ff;
  color: #274690;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
`;

const EmptyState = styled.div`
  padding: 18px;
  text-align: center;
  color: #6b7280;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const DangerBtn = styled.button`
  appearance: none;
  border: 1px solid #fca5a5;
  background: #fee2e2;
  color: #b91c1c;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #fecaca;
    border-color: #f87171;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  appearance: none;
  border: 1px solid #dbe3ef;
  background: #fff;
  color: #334155;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #f8fafc;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/* Modal potwierdzenia */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(9, 12, 20, 0.55);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 18px 20px;
  background: linear-gradient(90deg, #fef2f2, #fff);
  border-bottom: 1px solid #fde2e2;
  h3 {
    margin: 0;
    font-size: 18px;
    color: #991b1b;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ModalBody = styled.div`
  padding: 16px 20px;
  color: #374151;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 14px 20px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #eef2f7;
`;

const TopTabsBar = styled.div`
  display: flex;
  gap: 8px;
  margin: 0 0 16px;
`;

/* ===== komponent ===== */

const HistoryComponent = () => {
  const [importHistory, setImportHistory] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [year, setYear] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { _id, fileName }
  const [deleting, setDeleting] = useState(false);
  const [monthConfirmOpen, setMonthConfirmOpen] = useState(false);
  const [pendingMonthDelete, setPendingMonthDelete] = useState(null); // { year, month }
  const [monthDeleting, setMonthDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("coverage"); // "coverage" | "history"

  const StatusCell = ({ ok, labelYes = "Tak", labelNo = "Nie" }) => (
    <StatusChip
      $ok={ok}
      aria-label={ok ? labelYes : labelNo}
      title={ok ? labelYes : labelNo}
    >
      {ok ? <FaCheck /> : <FaTimes />}
    </StatusChip>
  );

  const askDeleteMonth = (y, m) => {
    setPendingMonthDelete({ year: y, month: m });
    setMonthConfirmOpen(true);
  };

  const cancelDeleteMonth = () => {
    setMonthConfirmOpen(false);
    setPendingMonthDelete(null);
    setMonthDeleting(false);
  };

  const confirmDeleteMonth = async () => {
    if (!pendingMonthDelete) return;
    setMonthDeleting(true);
    try {
      await axios.delete(
        `https://server229256.nazwa.pl/api/imports/${pendingMonthDelete.year}/${pendingMonthDelete.month}`,
        axiosCfg
      );

      // ✅ Refresh IndexedDB caches so dashboards read the new state
      await refreshSalesAndPurchasesCache();

      // refresh months coverage (and optionally history)
      await fetchCoverage(year);

      cancelDeleteMonth();
    } catch (err) {
      console.error(
        "❌ Błąd kasowania miesiąca:",
        err?.response?.data || err.message
      );
      setMonthDeleting(false);
      alert("Nie udało się usunąć danych dla tego miesiąca. Spróbuj ponownie.");
    }
  };

  const token = localStorage.getItem("token");
  const axiosCfg = { headers: { Authorization: token } };

  const fetchCoverage = async (y) => {
    const urlBase = "https://server229256.nazwa.pl/api/import-coverage";
    const url = y ? `${urlBase}?year=${y}` : urlBase;
    const { data } = await axios.get(url, axiosCfg);

    const fallbackYear = new Date().getFullYear();
    const safeYears = data?.availableYears?.length
      ? data.availableYears
      : [data.year || fallbackYear];

    const activeYear = data.year || y || fallbackYear;

    setAvailableYears(safeYears);
    setYear(activeYear);
    setMonths(data.months || []);
  };

  const fetchHistory = async () => {
    const res = await axios.get(
      "https://server229256.nazwa.pl/api/import-history",
      axiosCfg
    );
    setImportHistory(res.data || []);
  };

  useEffect(() => {
    fetchCoverage().catch(() => {});
    fetchHistory().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTabClick = (y) => {
    if (y === year) return;
    setYear(y);
    fetchCoverage(y).catch(() => {});
  };

  // lata od najstarszego do najnowszego
  const yearsAsc = [...availableYears].sort((a, b) => a - b);

  // akcje kasowania
  const askDelete = (row) => {
    setPendingDelete({ _id: row._id, fileName: row.fileName || "bez nazwy" });
    setConfirmOpen(true);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setPendingDelete(null);
    setDeleting(false);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleting(true);
    try {
      await axios.delete(
        `https://server229256.nazwa.pl/api/import-history/${pendingDelete._id}`,
        axiosCfg
      );
      // usuń lokalnie
      setImportHistory((prev) =>
        prev.filter((r) => r._id !== pendingDelete._id)
      );
      cancelDelete();
    } catch (err) {
      console.error(
        "Błąd kasowania historii:",
        err?.response?.data || err.message
      );
      setDeleting(false);
      // możesz tu dodać toast/alert
      alert("Nie udało się usunąć rekordu historii. Spróbuj ponownie.");
    }
  };
const refreshSalesAndPurchasesCache = async () => {
  const now = Date.now();

  // fetch fresh data in parallel
  const [salesRes, zakupyRes] = await Promise.all([
    axios.get("https://server229256.nazwa.pl/api/sprzedaz", axiosCfg),
    axios.get("https://server229256.nazwa.pl/api/zakupy", axiosCfg),
  ]);

  // ensure DB is ready
  await getDB();

  // overwrite caches (parallel)
  await Promise.all([
    dbPut("sales", "allSales", { data: salesRes.data, timestamp: now }),
    dbPut("zakupy", "allZakupy", { data: zakupyRes.data, timestamp: now }),
  ]);

  // let other screens know the cache changed
  window.dispatchEvent(new Event("data-cache-updated"));
};


  return (
    <CardNoClip>
      {/* Top-level tabs */}
      <TopTabsBar role="tablist" aria-label="Sekcje importów">
        <Tab
          role="tab"
          aria-selected={activeTab === "coverage"}
          $active={activeTab === "coverage"}
          onClick={() => setActiveTab("coverage")}
        >
          Pokrycie importów wg miesięcy
        </Tab>
        <Tab
          role="tab"
          aria-selected={activeTab === "history"}
          $active={activeTab === "history"}
          onClick={() => setActiveTab("history")}
        >
          Historia importów
        </Tab>
      </TopTabsBar>

      {/* ===== Tab: Pokrycie importów wg miesięcy ===== */}
      {activeTab === "coverage" && (
        <>
          <SectionTitle>Pokrycie importów wg miesięcy</SectionTitle>

          {/* Zakładki z latami (rosnąco) */}
          <TabsBar role="tablist" aria-label="Lata importów">
            {yearsAsc.map((y) => (
              <Tab
                key={y}
                role="tab"
                aria-selected={y === year}
                aria-controls={`panel-${y}`}
                $active={y === year}
                onClick={() => onTabClick(y)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTabClick(y);
                  }
                }}
              >
                {y}
              </Tab>
            ))}
          </TabsBar>

          <div id={`panel-${year}`} role="tabpanel" aria-labelledby={`${year}`}>
            <StickyTableWrap style={{ "--sticky-offset": "0px" }}>
              <MonthsTable>
                <thead>
                  <TableRow>
                    <TableHeader>Miesiąc</TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>
                      Sprzedaż (import)
                    </TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>
                      Zakupy (import)
                    </TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>
                      Akcje
                    </TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const hasAny = m.any;
                    return (
                      <TableRow key={m.month}>
                        <TableCell data-label="Miesiąc">
                          {monthNames[m.month - 1]}
                        </TableCell>
                        <TableCell
                          data-label="Sprzedaż (import)"
                          style={{ textAlign: "center" }}
                        >
                          <StatusCell ok={!!m.sprzedaz} />
                        </TableCell>
                        <TableCell
                          data-label="Zakupy (import)"
                          style={{ textAlign: "center" }}
                        >
                          <StatusCell ok={!!m.zakupy} />
                        </TableCell>
                        <TableCell
                          data-label="Akcje"
                          style={{ textAlign: "center" }}
                        >
                          <IconButton
                            onClick={() =>
                              hasAny && askDeleteMonth(year, m.month)
                            }
                            disabled={!hasAny}
                            title={
                              hasAny
                                ? "Usuń dane tego miesiąca"
                                : "Brak danych do usunięcia"
                            }
                            aria-disabled={!hasAny}
                          >
                            <FaTrash />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </MonthsTable>
            </StickyTableWrap>
          </div>
        </>
      )}

      {/* ===== Tab: Historia importów ===== */}
      {activeTab === "history" && (
        <>
          <SectionTitle>Historia importów</SectionTitle>
          <HistoryScroll>
            <HistoryTable>
              <thead>
                <TableRow>
                  <TableHeader>Nazwa pliku</TableHeader>
                  <TableHeader>Data importu</TableHeader>
                  <TableHeader>Użytkownik</TableHeader>
                  <TableHeader>Akcje</TableHeader>
                </TableRow>
              </thead>
              <tbody>
                {importHistory.length > 0 ? (
                  importHistory.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell data-label="Nazwa pliku">
                        {row.fileName || "—"}
                      </TableCell>
                      <TableCell data-label="Data importu">
                        {formatDatePL(row.importDate)}
                      </TableCell>
                      <TableCell data-label="Użytkownik">
                        {row.user ? <UserChip>{row.user}</UserChip> : "—"}
                      </TableCell>
                      <TableCell data-label="Akcje">
                        <ActionsCell>
                          <IconButton
                            onClick={() => askDelete(row)}
                            title="Usuń rekord"
                          >
                            <FaTrash />
                          </IconButton>
                        </ActionsCell>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan="4">
                      <EmptyState>Brak historii importów.</EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </HistoryTable>
          </HistoryScroll>
        </>
      )}

      {/* Modal potwierdzenia — usuwanie rekordu historii */}
      {confirmOpen && (
        <ModalOverlay onClick={cancelDelete}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Potwierdzenie usunięcia</h3>
            </ModalHeader>
            <ModalBody>
              <p style={{ marginTop: 0 }}>
                Czy na pewno chcesz usunąć{" "}
                <strong>rekord historii importu</strong>
                {pendingDelete?.fileName ? (
                  <>
                    {" "}
                    dla pliku <strong>„{pendingDelete.fileName}”</strong>
                  </>
                ) : null}
                ?
              </p>
              <p
                style={{
                  margin: 0,
                  color: "#9a3412",
                  background: "#fff7ed",
                  border: "1px solid #ffedd5",
                  padding: "10px 12px",
                  borderRadius: 8,
                }}
              >
                <strong>Uwaga:</strong> usuwasz jedynie wpis w historii.
                Powiązane dane (np. sprzedaż/zakupy){" "}
                <strong>nie będą usunięte</strong> i nadal będą widoczne w
                raportach.
              </p>
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={cancelDelete} disabled={deleting}>
                Anuluj
              </GhostBtn>
              <DangerBtn onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Usuwanie…" : "Usuń rekord"}
              </DangerBtn>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Modal potwierdzenia — usuwanie danych miesiąca (sprzedaż+zakupy) */}
      {monthConfirmOpen && (
        <ModalOverlay onClick={cancelDeleteMonth}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Potwierdzenie usunięcia danych</h3>
            </ModalHeader>
            <ModalBody>
              <p style={{ marginTop: 0 }}>
                Ta operacja <strong>usunie wszystkie rekordy</strong> już
                zaimportowane dla miesiąca{" "}
                <strong>
                  {monthNames[(pendingMonthDelete?.month ?? 1) - 1]}
                </strong>{" "}
                {pendingMonthDelete?.year}. Czy chcesz kontynuować?
              </p>
              <p
                style={{
                  margin: 0,
                  color: "#9a3412",
                  background: "#fff7ed",
                  border: "1px solid #ffedd5",
                  padding: "10px 12px",
                  borderRadius: 8,
                }}
              >
                <strong>Uwaga:</strong> usunięte dane (sprzedaż/zakupy){" "}
                <strong>znikną z raportów</strong>. Operacji nie można cofnąć.
              </p>
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={cancelDeleteMonth} disabled={monthDeleting}>
                Anuluj
              </GhostBtn>
              <DangerBtn onClick={confirmDeleteMonth} disabled={monthDeleting}>
                {monthDeleting ? "Usuwanie…" : "Usuń dane miesiąca"}
              </DangerBtn>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </CardNoClip>
  );
};

export default HistoryComponent;
